import { DestroyRef, inject, Injectable, signal, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MediaMatcher } from '@angular/cdk/layout';
import { filter, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Inject } from '@angular/core';

const BREAKPOINT_QUERIES = {
  XS: '(max-width: 320px)',
  XSM: '(min-width: 321px) and (max-width: 380px)',
  XSL: '(min-width: 381px) and (max-width: 420px)',
  SM: '(min-width: 421px) and (max-width: 640px)',
  MD: '(min-width: 641px) and (max-width: 768px)',
  LG: '(min-width: 769px) and (max-width: 1024px)',
  MAX_XL2: '(max-width: 1536px)',
  MAX_LG: '(max-width: 1024px)',
  XL: '(min-width: 1025px) and (max-width: 1280px)',
  XL2: '(min-width: 1281px) and (max-width: 1536px)',
  XL3: '(min-width: 1537px) and (max-width: 1920px)',
  XL4: '(min-width: 1921px)',
} as const;

@Injectable({
  providedIn: 'root',
})
export class UIService {
  private readonly router = inject(Router);
  private readonly mediaMatcher = inject(MediaMatcher);
  private readonly destroyRef = inject(DestroyRef);

  isShowChat = signal<boolean>(true);
  isShowNav = signal<boolean>(true);

  breakpoints = signal({
    xs: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XS).matches,
    xsm: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSM).matches,
    xsl: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSL).matches,
    sm: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.SM).matches,
    md: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MD).matches,
    lg: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.LG).matches,
    max_lg: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_LG).matches,
    xl: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL).matches,
    xl2: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL2).matches,
    max_xl2: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_XL2).matches,
    xl3: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL3).matches,
    xl4: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL4).matches,
  });

  constructor(@Inject(DOCUMENT) private document: Document) {
    const xs = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XS);
    const xsm = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSM);
    const xsl = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSL);
    const sm = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.SM);
    const md = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MD);
    const lg = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.LG);
    const max_lg = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_LG);
    const xl = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL);
    const xl2 = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL2);
    const max_xl2 = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_XL2);
    const xl3 = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL3);
    const xl4 = this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL4);
    const breakpoints = [xs, xsm, xsl, sm, md, xl, lg, xl2, xl3, xl4];

    // Initialize breakpoints based on current screen size

    breakpoints.forEach((bp) => {
      bp.onchange = () => {
        this.breakpoints.set({
          xs: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XS).matches,
          xsm: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSM).matches,
          xsl: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XSL).matches,
          sm: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.SM).matches,
          md: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MD).matches,
          lg: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.LG).matches,
          max_lg: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_LG)
            .matches,
          xl: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL).matches,
          xl2: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL2).matches,
          max_xl2: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.MAX_XL2)
            .matches,
          xl3: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL3).matches,
          xl4: this.mediaMatcher.matchMedia(BREAKPOINT_QUERIES.XL4).matches,
        });
      };
    });

    if (max_xl2.matches) {
      this.isShowChat.set(false);
    }
    if (max_lg.matches) {
      this.isShowNav.set(false);
    }

    max_xl2.onchange = (e) => {
      this.isShowChat.set(!e.matches);
      this.updateBodyScroll();
    };
    max_lg.onchange = (e) => {
      this.isShowNav.set(!e.matches);
      this.updateBodyScroll();
    };

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.url.split('/').at(-1)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if ((max_xl2.matches || max_lg.matches) && this.isShowChat()) {
          this.isShowChat.set(false);
          this.updateBodyScroll();
        }
        if (max_lg.matches && this.isShowNav()) {
          this.isShowNav.set(false);
          this.updateBodyScroll();
        }
      });
  }

  private updateBodyScroll() {
    const body = this.document.body;

    if (
      (this.breakpoints().max_xl2 && this.isShowChat()) ||
      (this.breakpoints().max_lg && this.isShowNav())
    ) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
  }

  toggleMobileState(state: 'chat' | 'nav') {
    if (state === 'chat') {
      if (this.isShowNav() && this.breakpoints().max_lg) {
        this.isShowNav.set(false);
      }
      this.isShowChat.update((prev) => !prev);
      this.updateBodyScroll();
    }

    if (state === 'nav') {
      if (this.isShowChat() && this.breakpoints().max_lg) {
        this.isShowChat.set(false);
        this.updateBodyScroll();
      }
      this.isShowNav.update((prev) => !prev);
      this.updateBodyScroll();
    }
  }

  toggleChat() {
    this.isShowChat.update((prev) => !prev);
    this.updateBodyScroll();
  }

  toggleNav() {
    this.isShowNav.update((prev) => !prev);
    this.updateBodyScroll();
  }
}
